import { PaymentServiceType } from '@dni-types/data-models/ecommerce-payment'
import '@dni-types/payment/applepayjs'
import { type Frames } from '@dni-types/payment/checkoutpay'
import { type ECPay } from '@dni-types/payment/ecpay'
import { type TPDirect } from '@dni-types/payment/tappay'
import { promiseRetry } from '@utils/promiseRetry'
import { TappayCreditCardFrameConfig } from '../constants/tappay'
import { PAYMENT_ERROR_CODE, PaymentError } from './PaymentError'
/**
 * Tappay
 */
/**
 * Remove all existing TapPay iframes from DOM elements
 * This prevents duplicate iframes when setupPaymentUi is called multiple times
 */
export const tappayRemoveExistingIframes = () => {
  const allFieldIds = Object.values(TappayCreditCardFrameConfig)

  allFieldIds.forEach((id) => {
    const element = document.getElementById(id)
    if (element) {
      const iframes = element.querySelectorAll('iframe')
      if (iframes.length > 0) {
        iframes.forEach(iframe => iframe.remove())
      }
    }
  })
}
const TAPPAY_SDK_URL = 'https://js.tappaysdk.com/sdk/tpdirect/v5.24.0'
const TAPPAY_SDK_LOAD_TIMEOUT = 15000
const TAPPAY_SDK_POLL_INTERVAL = 200
const TAPPAY_SDK_RETRY_COUNT = 3
const TAPPAY_SDK_RETRY_INTERVAL = 500

/**
 * timeout 是 terminal failure：script tag 還掛在 DOM 上輪詢了整整 15 秒都沒有 TPDirect，
 * 重試只會對同一具死 tag 再等 15 秒（tag 刻意不移除，見 loadScript 內註解），
 * 不像 error 事件屬暫時性網路失敗值得重試
 */
class TappaySdkLoadTimeoutError extends Error {}

let tappayScriptPromise: Promise<void> | null = null

export const loadTappayScript = () => {
  if (window.TPDirect) return Promise.resolve()
  if (tappayScriptPromise) return tappayScriptPromise

  const loadScript = () => new Promise<void>((resolve, reject) => {
    if (window.TPDirect) {
      resolve()
      return
    }

    let script = document.querySelector<HTMLScriptElement>(`script[src="${TAPPAY_SDK_URL}"]`)
    if (!script) {
      script = document.createElement('script')
      script.setAttribute('src', TAPPAY_SDK_URL)
      script.async = true
      document.querySelector('head')?.appendChild(script)
    }

    const scriptElement = script

    const pollTimer = window.setInterval(() => {
      if (window.TPDirect) settleLoaded()
    }, TAPPAY_SDK_POLL_INTERVAL)
    // timeout 不移除 script tag：classic script 的下載/執行無法被移除中斷，
    // 移除後重試會插入第二份 SDK，兩份先後執行會互相覆蓋 setupSDK 狀態
    const timeoutTimer = window.setTimeout(() => {
      cleanupTimers()
      reject(new TappaySdkLoadTimeoutError('[Tappay] SDK script load timed out'))
    }, TAPPAY_SDK_LOAD_TIMEOUT)

    function cleanupTimers() {
      window.clearInterval(pollTimer)
      window.clearTimeout(timeoutTimer)
    }
    function settleLoaded() {
      cleanupTimers()
      resolve()
    }

    // script tag 可能是其他 Tappay module 併發插入的，load 事件可能在掛 listener 前就已觸發，
    // 用輪詢補捉「事件已錯過但 TPDirect 已就緒」的狀態。
    // load 事件不代表 SDK 真的就緒（proxy/擋廣告可能回 200 的非 SDK 內容），一律驗過 TPDirect 才 resolve
    scriptElement.addEventListener('load', () => {
      if (window.TPDirect) settleLoaded()
    }, { once: true })
    scriptElement.addEventListener('error', () => {
      cleanupTimers()
      scriptElement.remove()
      reject(new Error('[Tappay] Failed to load SDK script'))
    }, { once: true })
  })

  const loadWithRetry = async (attemptsLeft: number): Promise<void> => {
    try {
      await loadScript()
    }
    catch (error) {
      if (error instanceof TappaySdkLoadTimeoutError || attemptsLeft <= 0) throw error
      console.warn('[Tappay] Failed to load SDK script, retrying...')
      await new Promise(resolve => window.setTimeout(resolve, TAPPAY_SDK_RETRY_INTERVAL))
      return loadWithRetry(attemptsLeft - 1)
    }
  }

  tappayScriptPromise = loadWithRetry(TAPPAY_SDK_RETRY_COUNT)
    .catch((error) => {
      // 重設 single-flight，讓之後的操作（如重選付款方式）能重新觸發載入
      tappayScriptPromise = null
      throw error
    })

  return tappayScriptPromise
}
export const isTappaySdkLoaded = (tappay?: TPDirect): tappay is TPDirect => {
  try {
    const isLoaded = tappay !== undefined
    if (!isLoaded) {
      throw new PaymentError('[TappaySdk] window.TPDirect is not loaded.', PaymentServiceType.TAPPAY, PAYMENT_ERROR_CODE.TAPPAY_SDK_NOT_LOADED)
    }
    return isLoaded
  }
  catch (error) {
    console.error('[Tappay Credit Card] window.Frames is not loaded.', error)
    return false
  }
}

/** Apple Pay */
const applePayVersionMap: Record<string, number> = {
  8.1: 1,
  9.0: 2,
  10.0: 3,
  10.3: 4,
  11.0: 5,
  11.3: 6,
  12.1: 7,
  13.0: 8,
  13.4: 9,
  14.0: 10,
  14.5: 11,
  15.0: 12,
  15.4: 13,
  16.0: 14,
}

export const getApplePayVersion = (): number | undefined => {
  const userAgent = navigator.userAgent || ''
  const defaultVersion = 6

  // Extract the iOS version from the user agent string
  const iOSVersionMatch = userAgent.match(/ OS[\s|_]?X?[\s|_]?(\d+)[._](\d+)?[._]?(\d+)?/i)
  if (!iOSVersionMatch) {
    console.error('[Apple pay] not Apple device')
    return undefined
  }

  const majorVersion = iOSVersionMatch[1]
  const minorVersion = iOSVersionMatch[2] || '0'
  const patchVersion = iOSVersionMatch[3] || '0'
  const iOSVersion = `${majorVersion}.${minorVersion}.${patchVersion}`

  const sortedVersions = Object.keys(applePayVersionMap).sort(
    (a, b) => parseFloat(a) - parseFloat(b),
  )
  for (let i = sortedVersions.length - 1; i >= 0; i--) {
    const version = sortedVersions[i]
    if (!version) continue
    if (parseFloat(iOSVersion) >= parseFloat(version)) {
      return applePayVersionMap[version]
    }
  }
  console.error('[Apple pay] default version returned')
  return defaultVersion
}

const applePaySupportedSnapshot = (() => {
  try {
    return !!(typeof window !== 'undefined' && window.ApplePaySession && ApplePaySession.canMakePayments())
  }
  catch (e) {
    console.error('[Apple Pay] Apple Pay is not supported', e)
    return false
  }
})()

export const isApplePaySupported = () => applePaySupportedSnapshot

/** Checkout Pay */
export const loadCheckoutPayScript = () => {
  const loadScript = () => new Promise<void>((resolve, reject) => {
    const framesScriptUrl = 'https://cdn.checkout.com/js/framesv2.min.js'
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${framesScriptUrl}"]`)

    // Only init script once
    // ref. https://github.com/checkout/frames-react/blob/master/src/frames/Frames.tsx
    if (!existingScript) {
      const script = document.createElement('script')
      script.setAttribute('src', framesScriptUrl)
      script.async = true

      const head = document.querySelector('head')
      head?.appendChild(script)

      script.onload = () => {
        resolve()
      }

      script.onerror = () => {
        reject(new Error('[Checkout Pay] Failed to load SDK script'))
      }
    }
    else {
      if (window.Frames) {
        resolve()
      }
      else {
        reject(new Error('[Checkout Pay] Failed to load SDK script'))
      }
    }
  })
  return promiseRetry({
    promiseFn: loadScript,
    retryMessage: '[Checkout Pay] Failed to load SDK script, retrying...',
  })
}
export const isCheckoutPayFramesLoaded = (frames?: Frames): frames is Frames => {
  try {
    const isLoaded = frames !== undefined
    if (!isLoaded) {
      throw new PaymentError('[Checkout Pay Credit Card] window.Frames is not loaded.', PaymentServiceType.CHECKOUTPAY, PAYMENT_ERROR_CODE.CHECKOUT_PAY_SDK_NOT_LOADED)
    }
    return isLoaded
  }
  catch (error) {
    console.error('[Checkout Pay Credit Card] window.Frames is not loaded.', error)
    return false
  }
}
/**
 * ECPay SDK 需要 jQuery 才能載入
 * window.$ = window.jQuery，assign window.$ 因為 ECPay 使用 $ 前綴
 * 不處理 jquery typing 以減少引入的檔案
 */
export const loadJqueryScript = () => {
  const loadScript = () => new Promise<void>((resolve, reject) => {
    const jQuerySdkUrl = 'https://code.jquery.com/jquery-3.5.1.min.js'
    // @ts-expect-error - TODO: fix this
    const jqueryIsLoaded = window.jQuery || window.$

    if (!jqueryIsLoaded) {
      const script = document.createElement('script')
      script.setAttribute('src', jQuerySdkUrl)
      script.async = true
      const head = document.querySelector('head')
      head?.appendChild(script)

      script.onload = () => {
        // @ts-expect-error - TODO: fix this
        window.$ = window.jQuery
        resolve()
      }

      script.onerror = () => {
        reject(new Error('[jQuery] Failed to load SDK script'))
      }
    }
    else {
      // @ts-expect-error - TODO: fix this
      if (window.jQuery) {
        // @ts-expect-error - TODO: fix this
        window.$ = window.jQuery
        resolve()
      }
      else {
        reject(new Error('[jQuery] Failed to load SDK script'))
      }
    }
  })
  return promiseRetry({
    promiseFn: loadScript,
    retryMessage: '[jQuery] Failed to load SDK script, retrying...',
  })
}

export const loadECPayScript = () => {
  const loadScript = () => new Promise<void>((resolve, reject) => {
    const ecPaySdkUrl = 'https://ecpg.ecpay.com.tw/Scripts/sdk-1.0.0.js?t=20210121100116'
    const existingScript = document.querySelector(`script[src="${ecPaySdkUrl}"]`)

    /**
     * Ref. https://github.com/ECPay/ECPayPaymentGatewayKit_Web/blob/main/Sample.html
     */
    if (!existingScript) {
      const script = document.createElement('script')
      script.setAttribute('src', ecPaySdkUrl)
      script.async = true
      const head = document.querySelector('head')
      head?.appendChild(script)

      script.onload = () => {
        resolve()
      }

      script.onerror = () => {
        reject(new Error('[ECPay] Failed to load SDK script'))
      }
    }
    else {
      if (window.ECPay) {
        resolve()
      }
      else {
        reject(new Error('[ECPay] Failed to load SDK script'))
      }
    }
  })
  return promiseRetry({
    promiseFn: loadScript,
    retryMessage: '[ECPay] Failed to load SDK script, retrying...',
  })
}

export const isECPayLoaded = (ecPay?: ECPay): ecPay is ECPay => {
  const isLoaded = ecPay !== undefined
  if (!isLoaded) {
    throw new Error('[ECPay Credit Card] no ECPay')
  }
  return isLoaded
}
